import React, { useEffect, ChangeEvent, useContext, useRef, useState } from 'react';
import useSetSearchTerm from 'hooks/useSetSearchTerm';
import {
  HomefieldIconArrowLeft,
  HomefieldIconSearch,
  HomefieldIconSignCircledCancelFilled,
} from '@dsg-tech/homefield-react-icons';
import { Divider, Popover } from '@dsg-tech/homefield-react';
import { getFeatureFlagByNameSelector, breakpointSelector } from 'store/selectors';
import {
  displayedSearchTermSelector,
  searchDataSelector,
  searchInputSelector,
  showMobileSaytDrawerSelector,
  showDesktopSaytDrawerSelector,
  showSaytDrawerSelector,
  showPreSaytDrawerSelector,
} from 'store/selectors/saytSelector';
import { StoreIdentifierName } from 'types';
import {
  setDisplayedSearchTerm,
  setFirePreSaytImpression,
  setShowMobileSaytDrawer,
  setShowDesktopSaytDrawer,
  setShowPreSaytDrawer,
  setShowSaytDrawer,
} from 'store/slices/sayt';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getCookie, setCookie } from 'utility/cookie-utils';
import { WindowContext } from 'utility/windowContext';
import { KEY_CODES } from 'utility/constants';
import { cleanSearchTerm } from 'utility/utility';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { handleSAYT20CharactersEvent } from 'utility/analytics-events';
import SAYTModalWrapper from './SAYTModalWrapper/SayTModalWrapper';
import SAYTSuggestions from './SAYTSuggestions/SAYTSuggestions';
import SAYTSuggestionsV2 from './SAYTSuggestionsV2/SAYTSuggestionsV2';
import PreSAYTSuggestions from './PreSAYTSuggestions/PreSAYTSuggestions';
import getSearchUtils from './SearchUtils';
import './SearchBar.scss';

type SearchBarProps = {
  headerRef?: React.RefObject<HTMLDivElement | null>;
  storeIdentifierName: StoreIdentifierName;
  thumbnailApi: string;
  mobileDrawer?: boolean;
};

const SearchBar = ({
  headerRef,
  storeIdentifierName,
  thumbnailApi,
  mobileDrawer,
}: SearchBarProps) => {
  const dispatch = useAppDispatch();
  const win = useContext(WindowContext);
  // checking to see if window object exitst, if it does we grab the search params for use later
  const search = win && window.location ? window.location.search : '';
  // if search params exist then we setup a new URLSearchParam object
  const params = search ? new URLSearchParams(search) : null;
  // utilize the search params object to get our searchTerm that was passed from the url, or set the initial search term to an empty string

  const inputRef = useRef<any>();
  const sayt20EventFired = useRef<boolean>(false);

  const clearButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const [showClearSearchPopover, setShowClearSearchPopover] = useState(false);
  const [showSearchButtonPopover, setShowSearchButtonPopover] = useState(false);
  const saytRedesignEnabled = useAppSelector(getFeatureFlagByNameSelector('saytRedesignEnabled'));
  const preSaytRedesignEnabled = useAppSelector(
    getFeatureFlagByNameSelector('preSaytRedesignEnabled'),
  );
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const searchInput = useAppSelector(searchInputSelector);
  const searchData = useAppSelector(searchDataSelector);
  const displayedSearchTerm = useAppSelector(displayedSearchTermSelector);
  const showSaytContent = useAppSelector(showSaytDrawerSelector);
  const showPresaytContent = useAppSelector(showPreSaytDrawerSelector);
  const showMobileSaytDrawer = useAppSelector(showMobileSaytDrawerSelector);
  const showDesktopSaytDrawer = useAppSelector(showDesktopSaytDrawerSelector);
  const [setSearchTerm] = useSetSearchTerm();
  const { submitSearch, updateSearchTermSAYT } = getSearchUtils(storeIdentifierName);

  const handleMobileSaytDrawer = () => {
    if (viewPortBreakpoint === 'mobile' && !showMobileSaytDrawer) {
      dispatch(setShowMobileSaytDrawer(true));
    }
  };

  const handleBackButtonMobile = () => {
    dispatch(setShowMobileSaytDrawer(false));
    dispatch(setShowPreSaytDrawer(false));
    dispatch(setShowSaytDrawer(false));
  };

  const SearchInputActivatedEventEmitter = (): void => {
    const searchInputActivatedEvent = new CustomEvent('SearchInputActivated');
    document.dispatchEvent(searchInputActivatedEvent);
    dispatch(setFirePreSaytImpression(false));
  };

  const updateSearchTerm = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length <= 75) {
      dispatch(setDisplayedSearchTerm(event.target.value));
      const cleanedTerm = cleanSearchTerm(event.target.value);
      setSearchTerm(cleanedTerm);

      // Handle showPresaytContent and showSaytContent
      if (event.target.value.length <= 2) {
        if (preSaytRedesignEnabled && saytRedesignEnabled) {
          dispatch(setShowPreSaytDrawer(true));
        }
        dispatch(setShowSaytDrawer(false));
      }

      // SAYT20Characters event and fire once per page view using the ref
      if (event.target.value.length >= 20 && !sayt20EventFired.current) {
        handleSAYT20CharactersEvent();
        sayt20EventFired.current = true;
      }
    }
  };

  const handleClear = (event?: React.KeyboardEvent) => {
    if (
      event?.key === KEY_CODES.ENTER ||
      event?.key === KEY_CODES.SPACE ||
      event?.key === KEY_CODES.ESCAPE ||
      event?.type === 'click'
    ) {
      setSearchTerm('');
      dispatch(setDisplayedSearchTerm(''));
      dispatch(setShowSaytDrawer(false));
      inputRef.current.value = '';
      if (viewPortBreakpoint === 'mobile' && !showMobileSaytDrawer) {
        inputRef?.current?.blur();
      } else {
        inputRef?.current?.focus();
      }
    }
  };

  const searchKeydown = (event: React.KeyboardEvent) => {
    if (event?.key === KEY_CODES.ENTER && searchInput.length >= 1) {
      const str = cleanSearchTerm(searchInput);
      // Prevent submission if the cleaned search term is empty (only contained whitespace)
      if (str.trim() !== '') {
        setCookie('enterSTerm', str);
        setCookie('sTermCookie', str);
        const searchUrl = submitSearch(str, 'enterSearchTerm');
        window.location.href = searchUrl;
      }
    }
  };

  const handleShowNewResultsDiv = () => {
    const hasFewCharacters = searchInput.length > 2 && searchInput.length < 20;
    const hasManyCharacters = searchInput.length >= 20;
    const hasKeywordSuggestions = (searchData?.suggestions?.keywords?.length ?? 0) > 0;
    const hasProductSuggestions = (searchData?.suggestions?.products?.length ?? 0) > 0;

    if (hasFewCharacters || hasManyCharacters) {
      if (
        (hasFewCharacters && hasKeywordSuggestions) ||
        (hasManyCharacters && (hasKeywordSuggestions || hasProductSuggestions))
      ) {
        dispatch(setShowPreSaytDrawer(false));
        dispatch(setShowSaytDrawer(true));
      } else {
        dispatch(setShowPreSaytDrawer(false));
        dispatch(setShowSaytDrawer(false));
      }
    }
  };

  const handleShowPreSayt = (value: boolean) => {
    dispatch(setShowPreSaytDrawer(value));
  };

  const handleInputFocus = () => {
    handleMobileSaytDrawer();
    if (inputRef.current?.value.length <= 2) {
      if (preSaytRedesignEnabled && saytRedesignEnabled) {
        dispatch(setShowPreSaytDrawer(true));
      }
      dispatch(setShowSaytDrawer(false));
    } else {
      dispatch(setShowPreSaytDrawer(false));
      setSearchTerm(inputRef.current.value);
      if (saytRedesignEnabled) {
        handleShowNewResultsDiv();
      } else {
        dispatch(setShowSaytDrawer(true));
      }
    }
  };

  // We don't want to resultsdiv panel to show when we are setting our searchTerm off our params.
  useEffect(() => {
    if (
      ((searchData?.suggestions && params && params.get('searchTerm') !== searchInput) ||
        (searchData?.suggestions && !params)) &&
      searchInput?.length > 2
    ) {
      if (saytRedesignEnabled) {
        handleShowNewResultsDiv();
      } else {
        dispatch(setShowSaytDrawer(true));
      }
    }
  }, [searchData.suggestions]);

  // This function calculates the absolute top position of an element with respect to the document, not the viewport. It does so by summing up the offsetTop of each parent element till the root.
  // It is used to determine the position of the search bar for sticky behavior.
  const getElementAbsoluteTop = (element: HTMLElement): number => {
    let top = 0;
    for (let el: HTMLElement | null = element; el; el = el.offsetParent as HTMLElement | null) {
      top += el.offsetTop;
    }
    return top;
  };

  const searchBarAbsoluteTop = useRef<number>(0);

  // This function is used to toggle the sticky class on the search bar based on the scroll position. On scroll up, the search bar becomes sticky, and on scroll down, it becomes unsticky.
  // This will run only for search pages for mobile and tablet viewports.
  // We need to use the dsg-header-search-sticky-enabled class as this is used in PLP code to manage the sticky filter button in PLP.

  // Skipping the first scroll event is a workaround to avoid handling scrolls caused by layout changes when the modal opens and closes, not user actions. This is done by setting the skipNextScroll ref to true.
  // The lastScrollTopRef is used to keep track of the last scroll position to determine if the user is scrolling up or down.
  // This pattern is widely used in the React/web community.

  const lastScrollTopRef = useRef(0);
  const skipNextScroll = useRef(false);
  useEffect(() => {
    if (
      (window.location.href.includes('/f/') ||
        window.location.href.includes('/search/SearchDisplay') ||
        getCookie('stickySearch') === 'true') &&
      (viewPortBreakpoint === 'mobile' || viewPortBreakpoint === 'tablet') &&
      !showMobileSaytDrawer
    ) {
      const searchBar = document.querySelectorAll(
        '.dsg-header-search-sticky-enabled',
      )[0] as HTMLElement;
      const searchBarHeight = searchBar?.clientHeight || 0;
      const stickyGhostDiv = document.querySelector(
        '#header-search-bar-sticky-ghost-div',
      ) as HTMLDivElement;

      searchBarAbsoluteTop.current = getElementAbsoluteTop(searchBar);

      lastScrollTopRef.current = window.scrollY || document.documentElement.scrollTop;
      skipNextScroll.current = true;

      const handleScroll = () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (skipNextScroll.current) {
          lastScrollTopRef.current = scrollTop;
          skipNextScroll.current = false;
          return;
        }

        const isScrollingUp = scrollTop <= lastScrollTopRef.current;
        if (isScrollingUp) {
          searchBar?.classList.add('sticky');
          if (stickyGhostDiv) {
            stickyGhostDiv.style.height = `${searchBarHeight}px`;
          }
        } else {
          searchBar?.classList.remove('sticky');
          if (stickyGhostDiv) {
            stickyGhostDiv.style.height = '0px';
          }
        }
        // Check if the search bar has reached its natural position. In that case, remove the sticky class.
        if (window.scrollY <= searchBarAbsoluteTop.current) {
          searchBar?.classList.remove('sticky');
          if (stickyGhostDiv) {
            stickyGhostDiv.style.height = '0px';
          }
        }
        lastScrollTopRef.current = scrollTop <= 0 ? 0 : scrollTop; // For negative scrolling
      };

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [viewPortBreakpoint, showMobileSaytDrawer]);

  useEffect(() => {
    if (mobileDrawer) {
      inputRef.current.focus();
    }
  }, []);

  const handleCloseModal = () => {
    dispatch(setShowDesktopSaytDrawer(false));
    dispatch(setShowPreSaytDrawer(false));
    dispatch(setShowSaytDrawer(false));
  };

  // This effect is used to manage the visibility of the SAYT modal based on the viewport. This ensures that resizing the window or changing the viewport will show/hide the SAYT modal accordingly.
  // It also ensures that the parent modal is controlled by the sayt and presayt content. If either of them is open, the parent modal will be open as well.
  useEffect(() => {
    if (viewPortBreakpoint === 'mobile') {
      if (showDesktopSaytDrawer) {
        dispatch(setShowDesktopSaytDrawer(false));
      }
      if (showMobileSaytDrawer) {
        return;
      }
      if (showSaytContent || showPresaytContent) {
        dispatch(setShowMobileSaytDrawer(true));
      }
    } else {
      if (showMobileSaytDrawer) {
        dispatch(setShowMobileSaytDrawer(false));
      }
      dispatch(setShowDesktopSaytDrawer(showSaytContent || showPresaytContent));
    }
  }, [viewPortBreakpoint, showPresaytContent, showSaytContent]);

  return (
    <div
      className={`search-bar-container-new hmf-px-s hmf-px-md-m hmf-pt-s hmf-pt-xlg-0${
        mobileDrawer ? ' mobile-drawer' : ''
      }`}>
      <div id="header-search-bar-sticky-anchor" className="hmf-display-block hmf-display-lg-none" />
      <div
        id="header-search-bar-sticky-ghost-div"
        className="hmf-display-block hmf-display-lg-none"
      />

      <div className="dsg-header-search-sticky-enabled">
        <div
          className={`search-container hmf-display-flex hmf-align-items-center ${
            mobileDrawer ? '' : 'hmf-pl-s'
          }`}
          id="searchBarContainer">
          {mobileDrawer ? (
            <button
              type="button"
              className="back-button hmf-p-xs"
              onClick={() => handleBackButtonMobile()}
              onKeyDown={(e) => handleBackButtonMobile()}
              aria-label="back to search results"
              tabIndex={0}>
              <HomefieldIconArrowLeft width="24px" height="24px" />
            </button>
          ) : (
            <HomefieldIconSearch
              width="20px"
              height="20px"
              className={`search-icon-container-left${
                searchInput?.length !== 0 ? ' header-search-icon-hidden' : ''
              }`}
            />
          )}
          <input
            data-testid="search-input"
            type="search"
            enterKeyHint="search"
            id="searchInput"
            className={`search-bar-input hmf-body-l hmf-pr-xxs${mobileDrawer ? ' hmf-pl-0' : ''}`}
            name="search"
            ref={inputRef}
            autoCapitalize="off"
            autoComplete="off"
            spellCheck="false"
            autoCorrect="off"
            maxLength={75}
            onClick={() => {
              SearchInputActivatedEventEmitter();
              handleInputFocus();
            }}
            onKeyDown={(e) => searchKeydown(e)}
            onChange={(e) => updateSearchTerm(e)}
            onFocus={handleInputFocus}
            placeholder="What are you looking for?"
            aria-label="Search"
            value={displayedSearchTerm}
          />
          <div
            className={`${'clear-search-icon-container-right hmf-display-flex hmf-align-items-center'}`}>
            <button
              className={`clear-button hmf-display-inline-flex hmf-justify-content-center hmf-align-items-center hmf-p-s ${
                searchInput?.length === 0 ? 'hidden' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleClear(e);
              }}
              onKeyDown={(e) => handleClear(e)}
              onFocus={() => setShowClearSearchPopover(true)}
              onBlur={() => setShowClearSearchPopover(false)}
              onMouseEnter={() => setShowClearSearchPopover(true)}
              onMouseLeave={() => setShowClearSearchPopover(false)}
              type="button"
              aria-label="clear search term"
              tabIndex={0}
              ref={clearButtonRef}>
              <HomefieldIconSignCircledCancelFilled height="16px" width="16px" />
            </button>
            {showClearSearchPopover && (
              <Popover
                className="popover-container"
                open={showClearSearchPopover}
                anchorElement={clearButtonRef?.current}
                onCloseModal={() => {}}>
                <div className="label-inverse-base hmf-body-m hmf-p-xs">Clear Search</div>
              </Popover>
            )}
            {searchInput?.length !== 0 && <Divider vertical />}
            <button
              className={`search-button hmf-display-inline-flex hmf-justify-content-center hmf-align-items-center hmf-p-xs ${
                searchInput?.length === 0 ? 'hidden' : ''
              }`}
              onClick={() => {
                // Prevent submission if the cleaned search term is empty (only contained whitespace)
                if (searchInput?.length !== 0 && cleanSearchTerm(searchInput).trim() !== '') {
                  window.location.href = submitSearch(
                    displayedSearchTerm !== searchInput ? displayedSearchTerm : searchInput,
                    'enterSearchTerm',
                  );
                }
              }}
              onKeyDown={(e) => searchKeydown(e)}
              onFocus={() => setShowSearchButtonPopover(true)}
              onBlur={() => setShowSearchButtonPopover(false)}
              onMouseEnter={() => setShowSearchButtonPopover(true)}
              onMouseLeave={() => setShowSearchButtonPopover(false)}
              type="button"
              aria-label="navigate to search results"
              tabIndex={0}
              ref={searchButtonRef}>
              <HomefieldIconSearch width="24" height="24" />
            </button>
            {showSearchButtonPopover && (
              <Popover
                className="popover-container"
                open={showSearchButtonPopover}
                anchorElement={searchButtonRef.current}
                onCloseModal={() => {}}>
                <div className="label-inverse-base hmf-body-m hmf-p-xs">Search</div>
              </Popover>
            )}
          </div>
        </div>
        {showDesktopSaytDrawer && viewPortBreakpoint !== 'mobile' && (
          <ErrorBoundaryWithLogger componentId="SearchBar-SAYTModalWrapper">
            <SAYTModalWrapper
              open={showDesktopSaytDrawer}
              onCloseModal={handleCloseModal}
              headerRef={headerRef}
              searchInputRef={inputRef}
              clearButtonRef={clearButtonRef}
              searchButtonRef={searchButtonRef}
              viewPortBreakpoint={viewPortBreakpoint}>
              {showSaytContent &&
                (saytRedesignEnabled ? (
                  <ErrorBoundaryWithLogger componentId="SAYTModalWrapper-SAYTSuggestionsV2">
                    <SAYTSuggestionsV2
                      storeIdentifierName={storeIdentifierName}
                      searchData={searchData}
                      submitSearch={submitSearch}
                      searchTerm={searchInput}
                      thumbnailApi={thumbnailApi}
                    />
                  </ErrorBoundaryWithLogger>
                ) : (
                  <ErrorBoundaryWithLogger componentId="SAYTModalWrapper-SAYTSuggestions">
                    <SAYTSuggestions
                      storeIdentifierName={storeIdentifierName}
                      thumbnailApi={thumbnailApi}
                      searchData={searchData}
                      submitSearch={submitSearch}
                      searchTerm={searchInput}
                      updateSearchTermSAYT={updateSearchTermSAYT}
                      onCloseModal={handleCloseModal}
                    />
                  </ErrorBoundaryWithLogger>
                ))}
              {preSaytRedesignEnabled && saytRedesignEnabled && showPresaytContent && (
                <ErrorBoundaryWithLogger componentId="SAYTModalWrapper-PreSAYTSuggestions">
                  <PreSAYTSuggestions
                    storeIdentifierName={storeIdentifierName}
                    handleShowPreSayt={handleShowPreSayt}
                  />
                </ErrorBoundaryWithLogger>
              )}
            </SAYTModalWrapper>
          </ErrorBoundaryWithLogger>
        )}
      </div>
    </div>
  );
};
export default SearchBar;
